'use client'

import { forwardRef, useEffect } from 'react'
import { I18nextProvider } from 'react-i18next'
import { usePathname } from 'next/navigation'
import i18n from '../app/i18n/i18n'
import { HREFLANG_LOCALES, getPathLocale } from '../app/i18n/supported-locales'

const normalizeLanguage = (value) => {
  if (!value) return null
  const language = String(value)

  if (HREFLANG_LOCALES.includes(language)) return language
  if (language.toLowerCase().startsWith('zh')) return 'zh-CN'

  const base = language.split('-')[0]
  return HREFLANG_LOCALES.includes(base) ? base : null
}

const readCookie = (name) => {
  if (typeof document === 'undefined') return null

  return (
    document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${name}=`))
      ?.split('=')
      .slice(1)
      .join('=') || null
  )
}

const readLocalStorage = (key) => {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

export const I18nClientProvider = forwardRef(({ children }, ref) => {
  const pathname = usePathname()

  // i18n is initialized to 'en' to match the server-rendered HTML. Detect the user's real
  // language AFTER hydration (using the already-registered detector, so config/caching are
  // identical) and switch to it — this avoids the server/client text hydration mismatch while
  // still honouring the browser language, ?lng=, and the persisted switcher choice.
  useEffect(() => {
    try {
      const detected = i18n.services?.languageDetector?.detect()
      const detectedLanguage = Array.isArray(detected) ? detected[0] : detected

      const lng =
        normalizeLanguage(getPathLocale(pathname)) ||
        normalizeLanguage(readCookie('language')) ||
        normalizeLanguage(readLocalStorage('language')) ||
        normalizeLanguage(readLocalStorage('i18nextLng')) ||
        normalizeLanguage(readCookie('i18next')) ||
        normalizeLanguage(detectedLanguage)

      if (lng && lng !== i18n.language) {
        i18n.changeLanguage(lng)
        document.documentElement.lang = lng
      }
    } catch {
      // detection is best-effort; English is a safe fallback
    }
  }, [pathname])

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
})
